import { useNavigation, useRoute } from '@react-navigation/native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { formatWithMask } from 'react-native-mask-input';

import {
  Button,
  ButtonsLayout,
  ButtonStyleEnum,
  Field,
  Form,
  FormButton,
  ScreenErrorView,
  ScreenLoader,
  Separator,
  SettingsLayout,
} from '@components';
import {
  ProfileNavigatorNavigationPropType,
  ProfileNavigatorRoutePropType,
  screens,
  ScreensNameType,
} from '@navigation';
import { IS_WEB, parseApiError } from '@utils';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { CREDIT_CARD_EXPIRATION_DATE_MASK } from '@constants';
import {
  UpdateCreditCardDataType,
  updateCreditCardSchema,
} from '@validation';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { ToastService } from '@services';

const FormContainer = styled(View)({
  maxWidth: ['100%', 600],
  flexGrow: [1, 0],
  pt: ['$5', '$6'],
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'flex-start',
});

const RowFieldContainer = styled(View)({
  flex: 1,
});

const cancelButtonStyle = IS_WEB
  ? ButtonStyleEnum.Outline
  : ButtonStyleEnum.Ghost;

function creditCardExpDateTransformer(value?: string) {
  return formatWithMask({
    text: value,
    mask: CREDIT_CARD_EXPIRATION_DATE_MASK,
  }).masked;
}

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Subscription,
  screens.CreditCards,
  screens.UpdateCreditCard,
];

type NavigationPropType =
  ProfileNavigatorNavigationPropType<'UpdateCreditCard'>;
type RoutePropType =
  ProfileNavigatorRoutePropType<'UpdateCreditCard'>;

export const UpdateCreditCardScreen = observer(() => {
  const { t } = useTranslation();
  const nav = useNavigation<NavigationPropType>();
  const viewer = useViewerModel();
  const route = useRoute<RoutePropType>();
  const item = viewer.creditCardList.getById(route.params.id);

  useNavOptionsSet({ title: t('updateCreditCard.title') });

  useAsyncInit(async () => {
    try {
      await viewer.creditCardList.fetchByIdAndMergeIntoEntity.run(
        route.params.id,
      );
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  async function updateCreditCard(values: UpdateCreditCardDataType) {
    try {
      await item?.update.run({ name: values.name });

      ToastService.showSuccess(t('updateCreditCard.successMessage'));

      closeUpdateCreditCard();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function closeUpdateCreditCard() {
    // We use navigate method here instead of goBack
    // to properly handle cancel button on web if page has been refreshed
    nav.navigate(screens.CreditCards);
  }

  if (viewer.creditCardList.fetchByIdAndMergeIntoEntity.isError) {
    return <ScreenErrorView />;
  }

  if (
    !item ||
    (viewer.creditCardList.fetchByIdAndMergeIntoEntity.inProgress &&
      !item)
  ) {
    return <ScreenLoader />;
  }

  const initialValues: UpdateCreditCardDataType = {
    name: item.name ?? '',
    number: item.formattedNumber,
    expDate: item.exp,
    cvc: '***',
    firstName: item.firstName,
    lastName: item.lastName,
  };

  return (
    <SettingsLayout
      title={t('updateCreditCard.title')}
      breadcrumbRoutes={BREADCRUMB_ROUTES}
    >
      <FormContainer>
        <Form<UpdateCreditCardDataType>
          initialValues={initialValues}
          onSubmit={updateCreditCard}
          validationSchema={updateCreditCardSchema}
        >
          <Field
            name="name"
            placeholder={t('updateCreditCard.cardName')}
          />

          <Separator />
          <Field
            name="number"
            placeholder={t('updateCreditCard.cardNumber')}
            keyboardType="number-pad"
            disabled
          />

          <Separator />

          <RowContainer>
            <RowFieldContainer>
              <Field
                name="expDate"
                placeholder="MM/YY"
                keyboardType="number-pad"
                valueTransformer={creditCardExpDateTransformer}
                disabled
              />
            </RowFieldContainer>

            <Separator vertical />

            <RowFieldContainer>
              <Field
                name="cvc"
                placeholder="CVC/CVV"
                keyboardType="number-pad"
                disabled
              />
            </RowFieldContainer>
          </RowContainer>

          <Separator />

          <RowContainer>
            <RowFieldContainer>
              <Field
                name="firstName"
                placeholder={t('updateCreditCard.firstName')}
                disabled
              />
            </RowFieldContainer>
            <Separator vertical />
            <RowFieldContainer>
              <Field
                name="lastName"
                placeholder={t('updateCreditCard.lastName')}
                disabled
              />
            </RowFieldContainer>
          </RowContainer>

          <Separator />

          <ButtonsLayout>
            <FormButton>{t('updateCreditCard.save')}</FormButton>

            <Button
              onPress={closeUpdateCreditCard}
              buttonStyle={cancelButtonStyle}
            >
              {t('updateCreditCard.cancel')}
            </Button>
          </ButtonsLayout>
        </Form>
      </FormContainer>
      <Separator size="$10" />
    </SettingsLayout>
  );
});
